import { Component, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { DynamicDialogRef, DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

import { DocSubmitTypeEnum, InvoiceDetail, InvoiceHeader } from 'app/model/entities/entity-model';
import { AgFns, GridOptionsExt } from './ag-fns';
import { UniAgFns } from './uni-ag-fns';
import { DbQueryService } from './db-query.service';
import { UtilFns } from './util-fns';

export interface InvoiceDialogParams {
  invoiceId: string;
}

/**
 * One billing leg of an InvoiceDetail. A detail carrying both a shipQty and a redistributeQty is
 * two billing events with different rate structures — the New leg (Unitec goods, sold at full
 * price) always includes the custom (add-on) amount, the Reissue leg (account-owned goods already
 * paid for, billed only a reissuance rate) includes it only when the JO sets
 * chargeAddonsOnReissuance. Both legs physically ship.
 * Splitting them into separate rows is what lets every row's extendedAmt be verified from the
 * cells to its left.
 */
export interface InvoiceLegRow {
  rowId: string;
  invoiceDetail: InvoiceDetail;
  legLabel: string;
  qty: number;
  unitPriceAmt: number;
  /** null on reissue legs — oversize does not apply, rendered as an em-dash. */
  unitOversizeAmt: number | null;
  unitCustomAmt: number;
  unitTotalAmt: number;
  extendedAmt: number;
}

@Component({
  selector: 'app-invoice-dialog',
  templateUrl: './invoice-dialog.component.html',
  standalone: false,
})
export class InvoiceDialogComponent implements OnInit {
  isPageReady = false;

  invoiceHeader: InvoiceHeader;
  invoiceDetails: InvoiceDetail[] = [];
  legRows: InvoiceLegRow[] = [];
  invdGridOptions: GridOptionsExt;
  api!: GridApi;
  employeeName: string;
  carrierName: string;
  trackingNumber: string;
  deliveredBy: string;
  savedToPdf: string;
  manifestName: string;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<InvoiceDialogParams>,
    private dbQueryService: DbQueryService,
  ) {}

  public static async openDialog(
    dialogService: DialogService,
    data: InvoiceDialogParams,
    config?: DynamicDialogConfig<InvoiceDialogParams>,
  ) {
    const ref = dialogService.open(InvoiceDialogComponent, {
      header: 'Invoice Information',
      closable: false,
      draggable: true,
      modal: true,
      width: '85vw',
      height: '85vh',
      focusOnShow: false,
      ...config,
      data: data,
    });
    return await firstValueFrom(ref.onClose);
  }

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params, 'InvoiceDialogParams');

    // rowData is synthetic leg rows, not entities — AG Grid's default getRowId reads data.id and
    // would collide the two legs of a single InvoiceDetail into one row node.
    this.invdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvdGridReady,
      getRowId: (params) => (params.data as InvoiceLegRow).rowId,
    });

    // Load the invoice with all related data
    this.invoiceHeader = await this.dbQueryService.getInvoice(params.invoiceId);

    if (this.invoiceHeader) {
      this.invoiceDetails = this.invoiceHeader.invoiceDetails || [];
      this.legRows = this.buildLegRows();
      const joHeader = this.invoiceHeader.joHeader;
      const name = [joHeader?.empFirstName, joHeader?.empLastName].filter(Boolean).join(' ');
      const email = joHeader?.empEmail ? `(${joHeader.empEmail})` : '';
      this.employeeName = [name, email].filter(Boolean).join(' ');

      // Invoice delivery info — posted invoices use persisted values; unposted use live account settings
      if (this.invoiceHeader.invoiceStatusId === 'POSTED') {
        this.deliveredBy = this.invoiceHeader.deliveredBy || '';
        this.savedToPdf = this.invoiceHeader.savedToPdf || 'No';
        this.manifestName = this.invoiceHeader.manifestName || 'None';
      } else {
        const account = joHeader?.account;
        const docType = account?.invoiceDocSubmitTypeId;
        switch (docType) {
          case DocSubmitTypeEnum.Print: this.deliveredBy = 'Print'; break;
          case DocSubmitTypeEnum.Email: this.deliveredBy = 'Email'; break;
          case DocSubmitTypeEnum.PrintAndEmail: this.deliveredBy = 'Print and Email'; break;
          case DocSubmitTypeEnum.None: this.deliveredBy = 'None'; break;
          default: this.deliveredBy = '';
        }
        this.savedToPdf = account?.saveOutputToPdf ? 'Yes' : 'No';
        this.manifestName = joHeader?.manifest?.manifestName || 'None';
      }

      // Shipping info
      const shipment = this.invoiceHeader.shipment;
      if (shipment) {
        this.carrierName = this.getServiceDisplayName(shipment.serviceApiIdentifier);
        this.trackingNumber = (shipment.trackingNumbers || shipment.trackingUrl)?.trim();
      } else {
        this.carrierName = 'Not Available';
        this.trackingNumber = 'Not Available';
      }
    }

    this.isPageReady = true;
  }

  /** Sum of both billing legs. Matches what ship-prep persists to invoiceHeader.subtotalAmt. */
  get subtotalAmt() {
    return (this.invoiceHeader.calcExtendedAmt() ?? 0) + (this.invoiceHeader.calcExtendedReissueAmt() ?? 0);
  }

  private buildLegRows(): InvoiceLegRow[] {
    const chargeAddons = this.invoiceHeader.joHeader?.chargeAddonsOnReissuance ?? false;
    const rows: InvoiceLegRow[] = [];

    this.invoiceDetails.forEach((invd) => {
      const jod = invd.joDetail;
      const shipQty = invd.shipQty ?? 0;
      const reissueQty = invd.redistributeQty ?? 0;

      // A detail with neither quantity still gets a shipped row so the line doesn't vanish.
      if (shipQty > 0 || reissueQty === 0) {
        rows.push({
          rowId: `${invd.id}-ship`,
          invoiceDetail: invd,
          legLabel: 'New',
          qty: shipQty,
          unitPriceAmt: jod?.unitPriceAmt ?? 0,
          unitOversizeAmt: jod?.unitOversizeAmt ?? 0,
          unitCustomAmt: jod?.unitCustomAmt ?? 0,
          unitTotalAmt: jod?.unitTotalAmt ?? 0,
          extendedAmt: (jod?.unitTotalAmt ?? 0) * shipQty,
        });
      }

      if (reissueQty > 0) {
        rows.push({
          rowId: `${invd.id}-reissue`,
          invoiceDetail: invd,
          legLabel: 'Reissue',
          qty: reissueQty,
          unitPriceAmt: jod?.unitReissueAmt ?? 0,
          unitOversizeAmt: null,
          unitCustomAmt: chargeAddons ? (jod?.unitCustomAmt ?? 0) : 0,
          unitTotalAmt: invd.calcUnitReissueAmt(chargeAddons),
          extendedAmt: invd.calcExtendedReissueAmt(chargeAddons),
        });
      }
    });

    return rows;
  }

  onInvdGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { ...UniAgFns.getManufacturerColDef('invoiceDetail.joDetail.product.productType.manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('invoiceDetail.joDetail.product.productType.style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('invoiceDetail.joDetail.product.productType.description') },
      { ...UniAgFns.getFeatureChoicesColDef('invoiceDetail.joDetail.product.featureChoicesExtract') },
      { headerName: 'Type', field: 'legLabel', ...AgFns.fixWidth(110) },
      { headerName: 'Qty', field: 'qty', ...AgFns.fixWidth(100) },
      { headerName: 'Price/Unit', field: 'unitPriceAmt', ...AgFns.fixWidth('money') },
      {
        headerName: 'Oversize Amt/Unit',
        field: 'unitOversizeAmt',
        valueFormatter: (params) => (params.value == null ? '—' : UtilFns.fmtCurrency(params.value)),
        ...AgFns.fixWidth('money'),
      },
      { headerName: 'Custom Amt/Unit', field: 'unitCustomAmt', ...AgFns.fixWidth('money') },
      { headerName: 'Total Amt/Unit', field: 'unitTotalAmt', ...AgFns.fixWidth('money') },
      { headerName: 'Extended Amt', field: 'extendedAmt', ...AgFns.fixWidth('money') },
    ];

    AgFns.initGrid(event, colDefs);
  }

  dbLookup(lookupName: string, value: string) {
    return this.dbQueryService.lookup(lookupName, value);
  }

  onClose() {
    this.ref.close(null);
  }

  /** Map service API identifier to display name */
  private getServiceDisplayName(serviceApiId: string): string {
    if (!serviceApiId) return null;
    const mapping: Record<string, string> = {
      ups_ground: 'UPS Ground',
      ups_next_day_air: 'UPS Next Day Air',
      ups_next_day_air_saver: 'UPS Next Day Air Saver',
      ups_2nd_day_air: 'UPS 2nd Day Air',
      ups_3_day_select: 'UPS 3 Day Select',
      usps_priority_mail: 'USPS Priority Mail',
      usps_first_class_mail: 'USPS First Class Mail',
      usps_parcel_select: 'USPS Parcel Select',
      usps_media_mail: 'USPS Media Mail',
      fedex_ground: 'FedEx Ground',
      fedex_home_delivery: 'FedEx Home Delivery',
      fedex_express_saver: 'FedEx Express Saver',
      fedex_2day: 'FedEx 2Day',
      fedex_standard_overnight: 'FedEx Standard Overnight',
      fedex_priority_overnight: 'FedEx Priority Overnight',
    };
    const key = serviceApiId.toLowerCase();
    if (mapping[key]) {
      return mapping[key];
    }
    // Fallback: format the code nicely (replace underscores, title case)
    return serviceApiId
      .split('_')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
      .join(' ');
  }
}
